/* ============================================================================
   Tour — ROTEIROS POR PAPEL
   ============================================================================
   O motor do tour (filtro, busca de alvo, espera, pausa, abertura de dropdown) é
   o MESMO para todo mundo. O que muda por papel é só a lista de passos. Este
   arquivo é o único lugar que sabe qual roteiro pertence a quem.

   Acrescentar um papel = escrever um roteiro e registrá-lo em ROTEIROS. Nada no
   orquestrador precisa mudar.
   ========================================================================== */

import admEscolar from "./admEscolar";
import aluno from "./aluno";
import professor from "./professor";

/** role_id no AVA. O 4 e o 6 são os mesmos números usados em layout/Header.js. */
export const ROLE_ADM_ESCOLAR = 4;
export const ROLE_PROFESSOR = 6;
export const ROLE_ALUNO = 7;

/**
 * Quanto esperar um alvo aparecer antes de desistir dele, em ms.
 *
 * As telas carregam por axios, então o alvo não está no DOM quando a rota monta.
 * 8s cobre uma listagem lenta sem deixar ninguém olhando para um tour paralisado.
 */
export const ESPERA_ALVO_MS = 8000;

/**
 * O FORMATO DE UM PASSO — vale para todos os roteiros.
 *
 * @property {string}  id                 chave estável. Não é índice: a numeração
 *                                        é calculada depois do filtro.
 * @property {string}  [alvo]             seletor CSS. Opcional quando o passo se
 *                                        acha por `alvoPorTexto` — é o caso de
 *                                        item de menu sem href, como o cabeçalho
 *                                        de uma seção com filhos.
 * @property {string[]} [alvoPorTexto]    nomes visíveis, como rede de segurança
 *                                        quando o href do backend não é o que
 *                                        supomos (ver ../alvo.js).
 * @property {string}  titulo
 * @property {string}  texto
 * @property {string}  [posicao]          posicionamento do balão (Joyride)
 * @property {boolean} verificarNoInicio  true para alvo que vive no DOM desde
 *                                        sempre (menu, header). Ausente na
 *                                        abertura = aquele perfil não tem a
 *                                        seção, e o passo é descartado antes de
 *                                        começar. Alvo de tela interna fica
 *                                        false: ele só existe depois de navegar,
 *                                        e quem cuida dele é `aguardarAlvo`.
 * @property {string}  [rotaEsperada]     quando presente, o passo é CLIQUE
 *                                        GUIADO: sem botão "Próximo", o tour
 *                                        avança quando a pessoa chega nesta rota.
 *                                        Ensina o caminho em vez de percorrê-lo
 *                                        por ela.
 * @property {boolean} [permiteCliqueNoAlvo] libera o clique no elemento
 *                                        destacado (o overlay bloqueia por
 *                                        padrão). Obrigatório em clique guiado.
 * @property {string}  [dependeDe]        id do passo que LEVA a esta tela. Se
 *                                        aquele cair no filtro, este cai junto,
 *                                        em cascata. Sem isto, um perfil sem a
 *                                        seção no menu ficaria com os passos
 *                                        seguintes órfãos, morrendo de timeout um
 *                                        a um na tela errada.
 */

const ROTEIROS = {
  [ROLE_ADM_ESCOLAR]: admEscolar,
  [ROLE_PROFESSOR]: professor,
  [ROLE_ALUNO]: aluno
};

/**
 * O roteiro do papel ativo, ou `null` se aquele papel não tem tour.
 *
 * `null` é o que decide se o ícone aparece no header (layout/HeaderMenu.js) e se
 * o orquestrador monta (../index.jsx) — os dois consultam a MESMA função, para
 * não existir gatilho que abre um tour vazio.
 */
export function roteiroDoPapel(roleId) {
  return ROTEIROS[roleId] || null;
}

/** Tem tour para este papel? */
export const temTour = roleId => roteiroDoPapel(roleId) !== null;
